import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';

import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrency, calculateDistance, formatDate } from '../../data/constants';
import { showToast } from "../../Components/utils/ShowToast";
import Loader from '../../Components/utils/Loader';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaGlobe, FaRuler, FaExclamationTriangle, FaChevronDown, FaChevronUp, FaBoxOpen, FaUser } from 'react-icons/fa';

const SellerTradeLead = () => {
  const { user, currency, exchangeRate } = useContext(LoginContext);
  const [tradeleads, setTradeleads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteModal, setQuoteModal] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ quotedAmount: '', message: '' });
  const [expandedReqs, setExpandedReqs] = useState(new Set());
  const socketRef = useRef(null);

  useEffect(() => { fetchTradeleads(); }, []);

  useEffect(() => {
    if (!user?._id) return;
    const socket = io(process.env.REACT_APP_Backend_Host_Name, { auth: { token: localStorage.getItem('token') } });
    socketRef.current = socket;
    socket.on('tradeleadListRefresh', () => fetchTradeleads());
    return () => socket.disconnect();
  }, [user?._id]);

  const fetchTradeleads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTradeleads(data.tradeLeads || []);
    } catch (error) {
      showToast('Error fetching tradeleads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.quotedAmount || quoteForm.quotedAmount <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads/offers`, { tradeleadId: quoteModal._id, ...quoteForm }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Quote sent successfully', 'success');
      setQuoteModal(null);
      setQuoteForm({ quotedAmount: '', message: '' });
      fetchTradeleads();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error sending quote', 'error');
    }
  };

  const toggleReqs = (id) => {
    setExpandedReqs(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Calculate distance between seller and buyer
  const getDistance = (tl) => {
    const sellerLoc = user?.sellerId?.location;
    const buyerLoc = tl.user?.location;
    if (!sellerLoc?.lat || !sellerLoc?.lon || !buyerLoc?.lat || !buyerLoc?.lon) return null;
    if (sellerLoc.lat === 0 && sellerLoc.lon === 0) return null;
    if (buyerLoc.lat === 0 && buyerLoc.lon === 0) return null;
    return calculateDistance(sellerLoc.lat, sellerLoc.lon, buyerLoc.lat, buyerLoc.lon);
  };

  // Check if seller is within buyer's preferred range
  const isWithinRange = (tl) => {
    const dist = getDistance(tl);
    if (dist === null || !tl.kilometersAway) return null;
    const range = tl.kilometersAway;
    if (range === '<10') return dist <= 10;
    if (range === '10-25') return dist <= 25;
    if (range === '25-50') return dist <= 50;
    if (range === '50-100') return dist <= 100;
    if (range === '100-500') return dist <= 500;
    if (range === '500+') return true;
    return null;
  };

  const getBuyerCountry = (tl) => {
    return tl.user?.buyerAddress?.country?.name || tl.country || '';
  };

  const getBuyerCity = (tl) => {
    return tl.city || tl.user?.buyerAddress?.city || '';
  };

  if (loading) return <Loader type="simple" />;

  return (
    <div className="stlContainer">
      <section className="mainSection">
        <div className="stlHeader">
          <div>
            <h1 className="pageMainHeading" style={{ textAlign: 'left' }}>Trade Leads</h1>
            <p className="stlSubtitle">Browse buyer requirements and send your best quotes</p>
          </div>
          <span className="stlCount">{tradeleads.length} active lead{tradeleads.length !== 1 ? 's' : ''}</span>
        </div>

        {tradeleads.length === 0 ? (
          <div className="stlEmptyState">
            <FaBoxOpen size={48} color="#d1d5db" />
            <h3>No Active Trade Leads</h3>
            <p>There are no trade leads available at the moment. Check back later!</p>
          </div>
        ) : (
          <div className="stlGrid">
            {tradeleads.map(tl => {
              const dist = getDistance(tl);
              const withinRange = isWithinRange(tl);
              const isExpanded = expandedReqs.has(tl._id);
              const isIntl = tl.sellingScope === 'international';
              const buyerCountry = getBuyerCountry(tl);
              const buyerCity = getBuyerCity(tl);

              return (
                <div key={tl._id} className="stlCard">
                  {/* Card Header */}
                  <div className="stlCardHeader">
                    <div className="stlBuyerInfo">
                      <div className="stlBuyerAvatar">
                        <FaUser size={14} />
                      </div>
                      <div>
                        <span className="stlBuyerName">{tl.user?.username || 'Buyer'}</span>
                        {buyerCountry && (
                          <span className="stlBuyerLocation">
                            <FaMapMarkerAlt size={10} />
                            {[buyerCity, buyerCountry].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="stlHeaderRight">
                      <span className={`stlScopeBadge ${isIntl ? 'international' : 'local'}`}>
                        <FaGlobe size={10} />
                        {isIntl ? 'International' : 'Local'}
                      </span>
                      <span className="stlPostedDate">{formatDate(tl.createdAt)}</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="stlCardBody">
                    <h3 className="stlProductName">{tl.product}</h3>
                    <div className="stlCategoryRow">
                      <span className="stlCategoryBadge">{tl.category}</span>
                      {tl.subcategory && <span className="stlCategoryBadge sub">{tl.subcategory}</span>}
                      {tl.event && <span className="stlEventBadge">📅 {tl.event}</span>}
                    </div>

                    {/* Requirements */}
                    <div className="stlRequirements">
                      <p className={`stlReqText ${isExpanded ? 'expanded' : ''}`}>
                        {tl.requirementDetails || 'No specific requirements mentioned.'}
                      </p>
                      {tl.requirementDetails?.length > 120 && (
                        <button className="stlExpandBtn" onClick={() => toggleReqs(tl._id)}>
                          {isExpanded ? <><FaChevronUp size={10} /> Less</> : <><FaChevronDown size={10} /> More</>}
                        </button>
                      )}
                    </div>

                    {/* Key Info Grid */}
                    <div className="stlInfoGrid">
                      <div className="stlInfoItem">
                        <span className="stlInfoLabel">Budget</span>
                        <span className="stlInfoValue budget">{formatCurrency(tl.budget, currency, exchangeRate)}</span>
                      </div>
                      <div className="stlInfoItem">
                        <span className="stlInfoLabel">Qty</span>
                        <span className="stlInfoValue">{tl.quantity}</span>
                      </div>
                      <div className="stlInfoItem">
                        <span className="stlInfoLabel"><FaCalendarAlt size={10} /> Needed</span>
                        <span className="stlInfoValue">{tl.neededDate ? formatDate(tl.neededDate) : '—'}</span>
                      </div>
                      <div className="stlInfoItem">
                        <span className="stlInfoLabel"><FaClock size={10} /> Time</span>
                        <span className="stlInfoValue">{tl.neededTime || '—'}</span>
                      </div>
                    </div>

                    {/* Location & Distance Section — only for local */}
                    {!isIntl && (
                      <div className="stlLocationSection">
                        <div className="stlLocationRow">
                          {tl.city && (
                            <span className="stlLocItem">
                              <FaMapMarkerAlt size={11} /> {[tl.city, tl.state].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {tl.kilometersAway && (
                            <span className="stlLocItem range">
                              <FaRuler size={11} /> Preferred: {tl.kilometersAway} km
                            </span>
                          )}
                          {dist !== null && (
                            <span className={`stlLocItem distance ${withinRange === false ? 'outOfRange' : 'inRange'}`}>
                              📍 {dist} km away
                            </span>
                          )}
                        </div>
                        {withinRange === false && (
                          <div className="stlRangeWarning">
                            <FaExclamationTriangle size={12} />
                            <span>You are outside the buyer's preferred range ({tl.kilometersAway} km)</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Delivery + Expiry */}
                    <div className="stlFooterMeta">
                      <span className="stlDeliveryBadge">{tl.deliveryOption}</span>
                      <span className="stlExpiryText">Expires: {tl.expiryDate ? formatDate(tl.expiryDate) : '—'}</span>
                    </div>
                  </div>

                  {/* Card Action */}
                  <div className="stlCardAction">
                    {tl.hasQuoted && tl.sentOffer ? (
                      <div className="stlSentOfferDetails" style={{ textAlign: 'left', width: '100%', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '13px', color: '#16a34a', fontWeight: 'bold', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ✓ Offer Sent
                        </div>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                          <strong>Amount:</strong> {formatCurrency(tl.sentOffer.quotedAmount, currency, exchangeRate)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#4b5563', whiteSpace: 'pre-wrap' }}>
                          <strong>Message:</strong> {tl.sentOffer.message || 'No message provided.'}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="stlQuoteBtn"
                        onClick={() => { setQuoteModal(tl); setQuoteForm({ quotedAmount: '', message: '' }); }}
                      >
                        Send Quote
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Quote Modal */}
      {quoteModal && (
        <div className="modalOverlay" onClick={() => setQuoteModal(null)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 className="stlQuoteTitle">Send Quote</h2>
            <p className="stlQuoteProduct">{quoteModal.product}</p>
            <div className="stlQuoteBuyerBudget">
              Buyer's budget: <strong>{formatCurrency(quoteModal.budget, currency, exchangeRate)}</strong>
            </div>
            <form onSubmit={handleQuoteSubmit} className="quoteForm">
              <div className="formGroup">
                <label className="modalLabel">Your Quote Amount (USD)</label>
                <input
                  type="number"
                  className="inputField"
                  value={quoteForm.quotedAmount}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, quotedAmount: e.target.value }))}
                  placeholder="Enter your quoted amount"
                  required
                  min="1"
                />
              </div>
              <div className="formGroup">
                <label className="modalLabel">Message to Buyer</label>
                <textarea
                  className="inputField"
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Describe your offer, experience, and why you're the best fit..."
                  rows={4}
                />
              </div>
              <div className="modalActions">
                <button type="button" className="primaryBtn3" onClick={() => setQuoteModal(null)}>Cancel</button>
                <button type="submit" className="primaryBtn">Send Quote</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerTradeLead;